import type { PermissionsMap } from 'src/pages/auth/hooks/permission-utils';

import { paths } from 'src/routes/paths';

import { checkHasPermission } from 'src/permissions/permission-engine';
import { getLoginPermissions } from 'src/pages/auth/hooks/usePermissionsSource';
import {
  getUserRole,
  hasBillingViewPermission,
} from 'src/pages/auth/hooks/permission-utils';

const SUPER_ADMIN = 'Super Admin';

/** Same rule as `PermissionWrapper` billing-only dashboard redirect. */
export function isBillingOnlyUser(permissions: PermissionsMap | null): boolean {
  if (!permissions || !hasBillingViewPermission(permissions)) return false;
  return Object.keys(permissions).every(
    (key) =>
      key === 'billing' ||
      key === 'bill' ||
      !Object.values(permissions[key] || {}).some((v) => v === true)
  );
}

/**
 * Ordered landing targets after login. First match wins (after dashboard + billing checks).
 * Module ids align with `withModulePermission` / nav `id` values.
 */
const POST_LOGIN_CANDIDATES: { path: string; module: string | string[] }[] = [
  { path: paths.account.root, module: 'company' },
  { path: paths.contact.root, module: 'contact' },
  { path: paths.chat.root, module: 'inbox' },
  { path: paths.campaign.root, module: 'campaign' },
  { path: paths.channel.root, module: 'channel' },
  { path: paths.ticket.root, module: 'ticket' },
  { path: paths.report['call-logs'], module: 'callreport' },
  { path: paths.automation.root, module: ['workflow', 'ai_agents', 'ai_analytics'] },
  { path: paths.setting.billing.root, module: 'billing' },
  { path: paths.profile, module: 'userprofile' },
  { path: paths.setting.root, module: 'setting' },
];

/**
 * Resolves where to send the user after a successful login (or auto-login).
 * - Super Admin → tenant list
 * - Billing-only → billing settings
 * - Else → dashboard if allowed, otherwise first module they can view
 * - Fallback → settings home (requires `user.view` on default settings index)
 */
export function getPostLoginRedirectPath(loginApiData: unknown): string {
  const role = getUserRole(loginApiData);

  if (role === SUPER_ADMIN) {
    return paths.tenant.root;
  }

  const map = getLoginPermissions(loginApiData);

  if (isBillingOnlyUser(map)) {
    return paths.setting.billing.root;
  }

  if (checkHasPermission(map, role, 'dashboard')) {
    return paths.dashboard.root;
  }

  for (const { path, module } of POST_LOGIN_CANDIDATES) {
    if (checkHasPermission(map, role, module)) {
      return path;
    }
  }

  return paths.setting.root;
}
